import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { decodeHtmlEntities } from "@theme/airbag-decode-html-entities";
import { fetchAirbagProductDetails } from "@theme/airbag-product-details-service";
import { readMmyVehicleContext } from "@theme/mmy-state";

/**
 * Product page tabs fed by Airbag Man product_details JSON (+ optional Shopify description fallback).
 *
 * @typedef {Object} AirbagTabsRefs
 * @property {HTMLElement} contentWrap
 * @property {HTMLElement} shopifyDescriptionFallback
 * @property {HTMLElement} tabAnnouncement
 * @property {HTMLElement} [panelHeading]
 * @property {HTMLElement} [contentSkeleton]
 *
 * @extends {Component<AirbagTabsRefs>}
 */
const TAB_DRAG_THRESHOLD_PX = 5;

class AirbagProductTabs extends Component {
  #requestId = 0;

  /** Product URL includes a collection (e.g. /collections/handle/products/handle). */
  #isCollectionContext() {
    return this.dataset.collectionInUrl === "true";
  }

  /**
   * MMY vehicle context is only used when the product was opened from a collection URL.
   * @returns {import("./mmy-state.js").MmyVehicleContext | null}
   */
  #effectiveMmyContext() {
    if (!this.#isCollectionContext()) return null;
    return readMmyVehicleContext();
  }

  /**
   * @param {import("./mmy-state.js").MmyVehicleContext | null} mmyContext
   * @returns {Record<string, string>}
   */
  #vehicleParamsFromContext(mmyContext) {
    if (!mmyContext) return {};
    return Object.fromEntries(
      Object.entries({
        make: mmyContext.make,
        model: mmyContext.model,
        cg: mmyContext.chassisGroupId || mmyContext.chassis,
        hg: mmyContext.height,
      }).filter(([, value]) => String(value || "").trim()),
    );
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #sanitizeText(value) {
    return this.#str(value).replace(/\s+/g, " ").trim();
  }

  /**
   * @returns {string}
   */
  #productHandleFromPath() {
    if (typeof window === "undefined") return "";
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return this.#str(match?.[1] ?? "");
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {Record<string, unknown>}
   */
  #buildPdfPayload(product) {
    const productNr = this.#currentNr();
    const productSku = this.#str(
      product.ProductNr ?? product.productNr ?? productNr,
    );
    const productTitle =
      this.#sanitizeText(this.dataset.productTitle) ||
      this.#str(product.VehicleName ?? product.vehicleName) ||
      this.#str(product.Title ?? product.title);
    const productDescription =
      this.#str(this.dataset.productDescription) ||
      this.#pick(
        product,
        "PRetailsDescription",
        "Description",
        "LongDescription",
        "ProductDescription",
      );
    const productImage = this.#str(this.dataset.productImage);
    const appProductDetails = {
      description: "",
      fitting_html: "",
    };

    const fitmentTime = this.#str(product.FitmentTime ?? product.FittingTime);
    if (fitmentTime) {
      appProductDetails.fitting_html = `<div class="product-fitment-time"><h3>Fitment Time:</h3><ul><li>${fitmentTime}</li></ul></div>`;
      appProductDetails.fitting = fitmentTime;
    }

    const vehicleName = this.#str(product.VehicleName ?? product.vehicleName);
    if (vehicleName) {
      appProductDetails.product_name = vehicleName;
    }

    const fitmentBits = [];
    const fitmentDescription = this.#str(product.PARetailsDescription);
    if (fitmentDescription) fitmentBits.push(fitmentDescription);
    for (const key of ["PAFootNote1", "PAFootNote2", "PAFootNote3", "PANote"]) {
      const value = this.#str(product[key]);
      if (value) fitmentBits.push(value);
    }
    if (fitmentBits.length > 0) {
      appProductDetails.fitment = fitmentBits.join("\n");
    }

    const application = this.#normalizeArray(product.Application);
    if (application.length > 0) {
      appProductDetails.application_content = application;
    }

    const minPressure = this.#str(this.dataset.serverMinPressure);
    if (minPressure) appProductDetails.min_pressure = minPressure;
    const maxPressure = this.#str(this.dataset.serverMaxPressure);
    if (maxPressure) appProductDetails.max_pressure = maxPressure;
    const weightRate = this.#str(this.dataset.serverAirbagHeight);
    if (weightRate) appProductDetails.weight_rate = weightRate;
    const warranty = this.#str(this.dataset.serverWarranty);
    if (warranty) appProductDetails.airbag_warranty = warranty;

    if (productNr.startsWith("RR") || productNr.startsWith("OA")) {
      appProductDetails.weight = "Vehicle Payload";
    }

    return {
      productDetails: {
        title: productTitle,
        productSKU: productSku,
        product_description: productDescription,
        product_image: productImage,
        parentSectionCount: 0,
        app_product_details: appProductDetails,
      },
      section1: {},
    };
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {Promise<string>}
   */
  async #requestGeneratedBrochure(product) {
    const payload = this.#buildPdfPayload(product);
    const response = await fetch("https://pdf.airbagman.com.au/generate-pdf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`PDF service returned HTTP ${response.status}`);
    }

    const raw = await response.text();
    /** @type {Record<string, unknown> | null} */
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      data = null;
    }

    const candidates = [
      data?.url,
      data?.pdfUrl,
      data?.pdf_url,
      data?.downloadUrl,
      data?.file,
      data?.path,
      data?.data?.url,
      data?.data?.pdfUrl,
      data?.data?.pdf_url,
      data?.data?.downloadUrl,
      data?.data?.path,
      data?.result?.url,
      data?.result?.pdfUrl,
      data?.result?.pdf_url,
      data?.result?.downloadUrl,
      raw,
    ];

    const toAbsoluteUrl = (value) => {
      const str = this.#str(value);
      if (!str) return "";
      if (/^https?:\/\//i.test(str)) return str;
      if (str.startsWith("/")) {
        try {
          return new URL(str, window.location.origin).toString();
        } catch {
          return "";
        }
      }
      return "";
    };

    const href = candidates
      .map((value) => toAbsoluteUrl(value))
      .find((value) => Boolean(value));
    if (!href) {
      throw new Error("PDF service did not return a download URL.");
    }
    return href;
  }

  /**
   * Open a URL in a new tab only (no same-page navigation).
   * @param {string} href
   */
  #openUrlInNewTab(href) {
    const resolvedHref = this.#str(href);
    if (!resolvedHref) return;

    const link = document.createElement("a");
    link.href = resolvedHref;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.className = "visually-hidden";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  /**
   * @param {string} label
   * @returns {{ anchor: HTMLAnchorElement; labelSpan: HTMLSpanElement }}
   */
  #createDownloadLinkElements(label) {
    const anchor = document.createElement("a");
    anchor.className = "airbag-product-tabs__download-link";
    anchor.rel = "noopener noreferrer";

    const iconWrap = document.createElement("span");
    iconWrap.className = "airbag-product-tabs__download-icon";
    iconWrap.setAttribute("aria-hidden", "true");
    const iconSvg = this.#cloneDownloadIconSvg();
    if (iconSvg) iconWrap.appendChild(iconSvg);

    const labelSpan = document.createElement("span");
    labelSpan.className = "airbag-product-tabs__download-label";
    labelSpan.textContent = label;

    anchor.append(iconWrap, labelSpan);
    return { anchor, labelSpan };
  }

  /**
   * @param {HTMLElement} container
   * @param {Record<string, unknown>} product
   */
  #appendGeneratedBrochureRow(container, product) {
    const partNr = this.#currentNr().toUpperCase();
    if (!/^(RR|CR|OA)/.test(partNr)) return;

    const defaultLabel =
      this.dataset.generatedBrochureLabel ||
      "Download Product Manual & Install Instructions";

    const ul = document.createElement("ul");
    ul.className = "airbag-product-tabs__link-list";
    const li = document.createElement("li");
    const { anchor, labelSpan } =
      this.#createDownloadLinkElements(defaultLabel);
    anchor.href = "#";
    anchor.setAttribute("aria-busy", "false");

    anchor.addEventListener("click", async (event) => {
      event.preventDefault();
      if (anchor.dataset.state === "loading") return;

      anchor.dataset.state = "loading";
      anchor.setAttribute("aria-busy", "true");
      labelSpan.textContent = "Generating PDF...";

      try {
        const href = await this.#requestGeneratedBrochure(product);
        this.#openUrlInNewTab(href);
        labelSpan.textContent = defaultLabel;
      } catch (error) {
        labelSpan.textContent =
          this.dataset.generatedBrochureError ||
          "Could not generate brochure. Please try again.";
      } finally {
        anchor.dataset.state = "idle";
        anchor.setAttribute("aria-busy", "false");
      }
    });

    li.appendChild(anchor);
    ul.appendChild(li);
    container.appendChild(ul);
  }

  /**
   * @param {HTMLElement | DocumentFragment} container
   * @param {string} href
   * @param {string} label
   */
  #appendDownloadLinkRow(container, href, label) {
    const resolvedHref = this.#str(href);
    const resolvedLabel = this.#str(label);
    if (!resolvedHref || !resolvedLabel) return;

    const ul = document.createElement("ul");
    ul.className = "airbag-product-tabs__link-list";
    const li = document.createElement("li");
    const { anchor: a } = this.#createDownloadLinkElements(resolvedLabel);
    a.href = resolvedHref;
    if (this.dataset.downloadsNewTab === "true") {
      a.target = "_blank";
    }
    li.appendChild(a);
    ul.appendChild(li);
    container.appendChild(ul);
  }

  /**
   * @returns {string}
   */
  #legacyManualUrl() {
    const partNr = this.#currentNr().toUpperCase();
    const legacyAbmPm = new Set([
      "RR4706",
      "RR4703",
      "RR4718",
      "RR4708",
      "RR4717",
      "RR4658",
      "RR4730",
      "RR4733",
      "RR4734",
      "RR4669",
      "RR4731",
      "RR4627",
    ]);

    if (partNr.startsWith("RR")) {
      return legacyAbmPm.has(partNr)
        ? "https://cdn.shopify.com/s/files/1/1921/8455/files/WABMPM_V6.pdf?"
        : "https://cdn.shopify.com/s/files/1/1921/8455/files/WRRPM_v7.pdf?";
    }
    if (partNr.startsWith("CR")) {
      return "https://cdn.shopify.com/s/files/1/1921/8455/files/WCRPM_V6.pdf?";
    }
    if (partNr.startsWith("OA")) {
      return "https://cdn.shopify.com/s/files/1/1921/8455/files/WOAPM_v6.pdf?";
    }
    if (partNr.startsWith("LU")) {
      return "https://cdn.shopify.com/s/files/1/1921/8455/files/WLUPM.pdf?";
    }
    return "";
  }

  /**
   * @param {HTMLElement | DocumentFragment} container
   */
  #appendLegacyManualAndInstructionRows(container) {
    const manualUrl = this.#legacyManualUrl();
    if (manualUrl) {
      this.#appendDownloadLinkRow(
        container,
        manualUrl,
        this.dataset.productManualLabel || "Product Manual",
      );
    }

    const installInstructionsUrl = this.#str(
      this.dataset.installInstructionsUrl,
    );
    if (installInstructionsUrl) {
      this.#appendDownloadLinkRow(
        container,
        installInstructionsUrl,
        this.dataset.installInstructionsLabel || "Installation Instructions",
      );
    }

    const operationDocumentUrl = this.#str(this.dataset.operationDocumentUrl);
    if (operationDocumentUrl) {
      this.#appendDownloadLinkRow(
        container,
        operationDocumentUrl,
        this.dataset.operationDocumentLabel || "Operation Document",
      );
    }
  }

  /** @type {ResizeObserver | null} */
  #tabScrollerResizeObserver = null;

  /** @type {{ stage: HTMLElement; startX: number; startScrollLeft: number; pointerId: number; dragging: boolean } | null} */
  #tabDragState = null;

  /** @type {AbortController | null} */
  #tabDragAbort = null;

  #suppressTabClick = false;

  /**
   * Tab strip & skeleton live outside `<airbag-product-tabs>` (sibling markup); resolve within this section.
   * @returns {HTMLElement | null}
   */
  #getStripRoot() {
    const sel = (this.dataset.stripRoot || "").trim();
    if (!sel) return null;
    const section = this.closest(".shopify-section");
    const el = section?.querySelector(sel);
    return el instanceof HTMLElement ? el : null;
  }

  /** @returns {HTMLElement | null} */
  #getTabsStageEl() {
    return (
      this.#getStripRoot()?.querySelector("[data-airbag-tabs-stage]") ?? null
    );
  }

  /** @returns {HTMLElement | null} */
  #getTablistEl() {
    return (
      this.#getStripRoot()?.querySelector(".airbag-product-tabs__tablist") ??
      null
    );
  }

  /** @returns {HTMLElement | null} */
  #getRootWrapEl() {
    const root = this.closest(".airbag-product-tabs__root");
    return root instanceof HTMLElement ? root : null;
  }

  /** @returns {SVGElement | null} */
  #cloneDownloadIconSvg() {
    const root = this.#getRootWrapEl();
    const tpl = root?.querySelector("template[data-airbag-download-icon]");
    if (!(tpl instanceof HTMLTemplateElement)) return null;
    const svg = tpl.content.querySelector("svg");
    if (!(svg instanceof SVGSVGElement)) return null;
    return /** @type {SVGElement} */ (svg.cloneNode(true));
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    window.addEventListener("storage", this.#onMmyContextChange);
    document.addEventListener("mmy:apply-filter", this.#onMmyContextChange);
    this.#bindTabs();
    this.#bindTabScroller();
    this.#load();
  }

  /** @param {Event} event */
  #onMmyContextChange = (event) => {
    if (
      event.type === "storage" &&
      event instanceof StorageEvent &&
      event.key !== "mmy"
    ) {
      return;
    }
    this.#load();
  };

  disconnectedCallback() {
    document.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
    );
    window.removeEventListener("storage", this.#onMmyContextChange);
    document.removeEventListener("mmy:apply-filter", this.#onMmyContextChange);
    const tablist = this.#getTablistEl();
    if (tablist) {
      tablist.removeEventListener("click", this.#onTablistClick);
      tablist.removeEventListener("keydown", this.#onTablistKeydown);
      delete tablist.dataset.airbagTabsBound;
    }
    this.#unbindTabScroller();
    this.#requestId += 1;
    super.disconnectedCallback();
  }

  /**
   * @param {Event & { detail?: { resource?: unknown; data?: { productId?: string | number; newProduct?: { id?: string } } } }} event
   */
  #onVariantUpdate = (event) => {
    const source = this.dataset.productNrSource;
    if (source !== "sku" && source !== "barcode") return;

    const detail = event.detail;
    const expectedPid = this.dataset.productId;
    const evtPid = detail?.data?.productId;
    if (
      evtPid != null &&
      expectedPid != null &&
      String(evtPid) !== String(expectedPid)
    ) {
      return;
    }

    const resource = detail?.resource;
    if (!resource || typeof resource !== "object") return;

    const variant = /** @type {{ sku?: string; barcode?: string }} */ (
      resource
    );
    this.dataset.initialProductNr =
      source === "sku"
        ? String(variant.sku ?? "")
        : String(variant.barcode ?? "");
    this.#load();
  };

  /**
   * Delegated handlers so tab/panel nodes stay in sync after DOM updates; always query panels on activate.
   * @param {MouseEvent} event
   */
  #onTablistClick = (event) => {
    if (this.#suppressTabClick) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#suppressTabClick = false;
      return;
    }

    const tablist = event.currentTarget;
    if (!(tablist instanceof HTMLElement)) return;
    const btn =
      event.target instanceof Element
        ? event.target.closest(".airbag-product-tabs__tab")
        : null;
    if (
      !(btn instanceof HTMLButtonElement) ||
      btn.getAttribute("role") !== "tab"
    )
      return;
    if (!tablist.contains(btn)) return;
    if (btn.disabled || btn.hidden) return;

    const tabsStage = this.#getTabsStageEl();
    if (!(tabsStage instanceof HTMLElement)) return;
    const tabs = Array.from(tabsStage.querySelectorAll('[role="tab"]'));
    const i = tabs.indexOf(btn);
    if (i >= 0) this.#activateTab(i);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onTablistKeydown = (event) => {
    const tablist = event.currentTarget;
    if (!(tablist instanceof HTMLElement)) return;
    if (
      !(event.target instanceof HTMLButtonElement) ||
      event.target.getAttribute("role") !== "tab"
    )
      return;

    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
    if (!keys.includes(event.key)) return;

    const tabsStage = this.#getTabsStageEl();
    if (!(tabsStage instanceof HTMLElement)) return;
    const tabs = Array.from(tabsStage.querySelectorAll('[role="tab"]'));
    const index = tabs.indexOf(event.target);
    if (index < 0) return;

    event.preventDefault();
    let next = index;

    if (event.key === "ArrowRight") {
      next = this.#nextAvailableTabIndex(tabs, index, 1);
    } else if (event.key === "ArrowLeft") {
      next = this.#nextAvailableTabIndex(tabs, index, -1);
    } else if (event.key === "Home") {
      next = this.#firstAvailableTabIndex(tabs);
    } else if (event.key === "End") {
      next = this.#lastAvailableTabIndex(tabs);
    }

    this.#activateTab(next);
    const btn = tabs[next];
    if (btn instanceof HTMLButtonElement && !btn.disabled) btn.focus();
  };

  #bindTabs() {
    const tablist = this.#getTablistEl();
    if (
      !(tablist instanceof HTMLElement) ||
      tablist.dataset.airbagTabsBound === "true"
    )
      return;
    tablist.dataset.airbagTabsBound = "true";
    tablist.addEventListener("click", this.#onTablistClick);
    tablist.addEventListener("keydown", this.#onTablistKeydown);
  }

  #bindTabScroller() {
    const stage = this.#getTabsStageEl();
    if (
      !(stage instanceof HTMLElement) ||
      stage.dataset.airbagTabsScrollerBound === "true"
    ) {
      return;
    }

    stage.dataset.airbagTabsScrollerBound = "true";
    stage.addEventListener("pointerdown", this.#onTabsStagePointerDown);
    stage.addEventListener("wheel", this.#onTabsStageWheel, { passive: false });

    this.#tabScrollerResizeObserver = new ResizeObserver(() => {
      this.#updateTabScrollerState();
    });
    this.#tabScrollerResizeObserver.observe(stage);

    requestAnimationFrame(() => {
      this.#updateTabScrollerState();
    });
  }

  #unbindTabScroller() {
    const stage = this.#getTabsStageEl();

    this.#tabScrollerResizeObserver?.disconnect();
    this.#tabScrollerResizeObserver = null;
    this.#endTabDrag();

    if (stage instanceof HTMLElement) {
      stage.removeEventListener("pointerdown", this.#onTabsStagePointerDown);
      stage.removeEventListener("wheel", this.#onTabsStageWheel);
      delete stage.dataset.airbagTabsScrollerBound;
      delete stage.dataset.scrollable;
      delete stage.dataset.dragging;
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onTabsStagePointerDown = (event) => {
    const stage = event.currentTarget;
    if (!(stage instanceof HTMLElement)) return;
    if (event.button !== 0) return;
    if (stage.scrollWidth <= stage.clientWidth) return;

    this.#endTabDrag();

    this.#tabDragState = {
      stage,
      startX: event.clientX,
      startScrollLeft: stage.scrollLeft,
      pointerId: event.pointerId,
      dragging: false,
    };

    this.#tabDragAbort = new AbortController();
    const opts = { signal: this.#tabDragAbort.signal };

    stage.addEventListener("pointermove", this.#onTabsStagePointerMove, opts);
    stage.addEventListener("pointerup", this.#onTabsStagePointerUp, opts);
    stage.addEventListener("pointercancel", this.#onTabsStagePointerUp, opts);
  };

  /**
   * @param {PointerEvent} event
   */
  #onTabsStagePointerMove = (event) => {
    const drag = this.#tabDragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;

    if (!drag.dragging) {
      if (Math.abs(deltaX) < TAB_DRAG_THRESHOLD_PX) return;

      drag.dragging = true;
      drag.stage.setAttribute("data-dragging", "");

      if (drag.stage.hasPointerCapture(event.pointerId) === false) {
        try {
          drag.stage.setPointerCapture(event.pointerId);
        } catch {
          /* Pointer may already be released in edge cases */
        }
      }
    }

    event.preventDefault();
    drag.stage.scrollLeft = drag.startScrollLeft - deltaX;
  };

  /**
   * @param {PointerEvent} event
   */
  #onTabsStagePointerUp = (event) => {
    const drag = this.#tabDragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const wasDragging = drag.dragging;

    if (drag.stage.hasPointerCapture(event.pointerId)) {
      drag.stage.releasePointerCapture(event.pointerId);
    }

    drag.stage.removeAttribute("data-dragging");
    this.#endTabDrag();

    if (wasDragging) {
      this.#suppressTabClick = true;
    }
  };

  #endTabDrag() {
    this.#tabDragAbort?.abort();
    this.#tabDragAbort = null;

    const drag = this.#tabDragState;
    if (drag) {
      drag.stage.removeAttribute("data-dragging");
      if (drag.dragging && drag.stage.hasPointerCapture(drag.pointerId)) {
        drag.stage.releasePointerCapture(drag.pointerId);
      }
    }

    this.#tabDragState = null;
  }

  /**
   * @param {WheelEvent} event
   */
  #onTabsStageWheel = (event) => {
    const stage = event.currentTarget;
    if (!(stage instanceof HTMLElement)) return;
    if (stage.scrollWidth <= stage.clientWidth) return;
    if (event.deltaY === 0) return;

    event.preventDefault();
    stage.scrollLeft += event.deltaY;
  };

  #updateTabScrollerState() {
    const stage = this.#getTabsStageEl();
    if (!(stage instanceof HTMLElement)) return;

    const overflowing = stage.scrollWidth > stage.clientWidth + 1;
    stage.toggleAttribute("data-scrollable", overflowing);
  }

  /**
   * @param {Element} btn
   */
  #tabButtonUnavailable(btn) {
    return !(btn instanceof HTMLButtonElement && !btn.hidden && !btn.disabled);
  }

  /**
   * @param {Element[]} tabs
   * @param {number} fromIndex
   * @param {number} delta 1 or -1
   */
  #nextAvailableTabIndex(tabs, fromIndex, delta) {
    const len = tabs.length;
    if (len === 0) return fromIndex;
    let idx = fromIndex;
    for (let tries = 0; tries < len; tries++) {
      idx = (idx + delta + len) % len;
      if (!this.#tabButtonUnavailable(tabs[idx])) return idx;
    }
    return fromIndex;
  }

  /**
   * @param {Element[]} tabs
   */
  #firstAvailableTabIndex(tabs) {
    const i = tabs.findIndex((t) => !this.#tabButtonUnavailable(t));
    return i >= 0 ? i : 0;
  }

  /**
   * @param {Element[]} tabs
   */
  #lastAvailableTabIndex(tabs) {
    for (let i = tabs.length - 1; i >= 0; i--) {
      if (!this.#tabButtonUnavailable(tabs[i])) return i;
    }
    return 0;
  }

  /** While fetching: secondary tabs disabled, skeleton visible, panels hidden via aria-busy. */
  #enterLoadingUi() {
    this.setAttribute("aria-busy", "true");
    const sk = this.refs.contentSkeleton;
    if (sk instanceof HTMLElement) sk.hidden = false;

    const ts = this.#getTabsStageEl();
    if (!(ts instanceof HTMLElement)) return;

    const tabs = Array.from(ts.querySelectorAll('[role="tab"]'));
    for (const [i, tab] of tabs.entries()) {
      if (!(tab instanceof HTMLButtonElement)) continue;
      if (i === 0) {
        tab.disabled = false;
        tab.removeAttribute("aria-disabled");
      } else {
        tab.disabled = true;
        tab.setAttribute("aria-disabled", "true");
      }
    }

    this.#activateTab(0);
  }

  /** After load: hide skeleton, clear busy state (tab disabled state from #applyTabVisibility). */
  #exitLoadingUi() {
    this.setAttribute("aria-busy", "false");
    const sk = this.refs.contentSkeleton;
    if (sk instanceof HTMLElement) sk.hidden = true;
    requestAnimationFrame(() => {
      this.#updateTabScrollerState();
    });
  }

  /**
   * @param {number} index
   */
  #activateTab(index) {
    const tabsStage = this.#getTabsStageEl();
    const contentWrap = this.refs.contentWrap;
    if (
      !(tabsStage instanceof HTMLElement && contentWrap instanceof HTMLElement)
    )
      return;

    const tabs = Array.from(tabsStage.querySelectorAll('[role="tab"]'));
    const panels = Array.from(
      contentWrap.querySelectorAll('[role="tabpanel"]'),
    );
    if (tabs.length === 0 || panels.length === 0) return;

    const clamped = Math.max(
      0,
      Math.min(index, Math.min(tabs.length, panels.length) - 1),
    );

    const targetTab = tabs[clamped];
    if (
      targetTab instanceof HTMLButtonElement &&
      (targetTab.disabled || targetTab.hidden)
    ) {
      return;
    }

    for (const [i, tab] of tabs.entries()) {
      const on = i === clamped;
      tab.setAttribute("aria-selected", String(on));
      tab.tabIndex = on ? 0 : -1;
    }

    for (const [i, panel] of panels.entries()) {
      const on = i === clamped;
      panel.hidden = !on;
      panel.toggleAttribute("inert", !on);
    }

    const label = tabs[clamped]?.textContent?.trim() ?? "";
    if (this.refs.tabAnnouncement && label) {
      this.refs.tabAnnouncement.textContent = label;
    }
    const heading = this.refs.panelHeading;
    if (heading instanceof HTMLElement && label) {
      heading.textContent = label;
    }

    if (targetTab instanceof HTMLButtonElement) {
      this.#scrollTabIntoView(targetTab);
    }

    requestAnimationFrame(() => {
      this.#updateTabScrollerState();
    });
  }

  /**
   * Keep the active tab visible when the strip overflows horizontally.
   * @param {HTMLButtonElement} tab
   */
  #scrollTabIntoView(tab) {
    const stage = this.#getTabsStageEl();
    if (!(stage instanceof HTMLElement)) return;
    if (stage.scrollWidth <= stage.clientWidth) return;

    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches
      ? "auto"
      : "smooth";

    tab.scrollIntoView({
      behavior,
      block: "nearest",
      inline: "nearest",
    });
  }

  #showSection() {
    const strip = this.#getStripRoot();
    const root = this.#getRootWrapEl();
    this.hidden = false;
    if (strip) strip.hidden = false;
    if (root) root.hidden = false;
  }

  #hideSection() {
    const strip = this.#getStripRoot();
    const root = this.#getRootWrapEl();
    this.hidden = true;
    if (strip) strip.hidden = true;
    if (root) root.hidden = true;
  }

  /**
   * @param {HTMLElement} panel
   */
  #panelHasContent(panel) {
    const body = panel.querySelector("[data-airbag-tab-panel]");
    if (!(body instanceof HTMLElement)) return false;
    if (!body.textContent?.trim()) return false;
    const hasRealNode = body.querySelector(
      ":scope > :not(.airbag-product-tabs__empty)",
    );
    return Boolean(hasRealNode);
  }

  /**
   * Hide tabs that have no content and activate first visible tab.
   * @returns {number}
   */
  #applyTabVisibility() {
    const tabsStage = this.#getTabsStageEl();
    const contentWrap = this.refs.contentWrap;
    if (
      !(tabsStage instanceof HTMLElement && contentWrap instanceof HTMLElement)
    )
      return 0;

    const tabs = Array.from(tabsStage.querySelectorAll('[role="tab"]'));
    const visibleIndices = [];

    for (const tab of tabs) {
      if (tab instanceof HTMLButtonElement) {
        tab.disabled = false;
        tab.removeAttribute("aria-disabled");
      }
    }

    for (const [index, tab] of tabs.entries()) {
      const panelId = tab.getAttribute("aria-controls") || "";
      const panel = panelId
        ? contentWrap.querySelector(`#${CSS.escape(panelId)}`)
        : null;
      const hasContent =
        panel instanceof HTMLElement ? this.#panelHasContent(panel) : false;

      tab.hidden = !hasContent;
      tab.tabIndex = hasContent ? 0 : -1;
      if (!hasContent) {
        tab.setAttribute("aria-selected", "false");
      }

      if (panel instanceof HTMLElement) {
        panel.hidden = !hasContent;
        panel.toggleAttribute("inert", !hasContent);
      }

      if (hasContent) {
        visibleIndices.push(index);
      }
    }

    if (visibleIndices.length > 0) {
      this.#activateTab(visibleIndices[0]);
    }

    return visibleIndices.length;
  }

  #currentNr() {
    return (this.dataset.initialProductNr || "").trim();
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {string[]} keys
   */
  #pick(product, ...keys) {
    for (const key of keys) {
      const v = this.#str(product[key]);
      if (v) return v;
    }
    return "";
  }

  /**
   * @param {unknown} value
   */
  #str(value) {
    if (value == null) return "";
    return String(value).trim();
  }

  /**
   * @param {unknown} raw
   */
  #normalizeArray(raw) {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    return [raw];
  }

  /**
   * @param {string} value
   */
  #normalizeLabel(value) {
    return this.#str(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  /**
   * @param {string} heading
   */
  #normalizeHeading(heading) {
    return this.#normalizeLabel(heading).replace(/\s+/g, " ");
  }

  /**
   * Parse HTML description into heading-based sections (h1–h3), including headings nested inside
   * wrappers such as `<div class="rte">…</div>`. Uses the same regex idea as the legacy theme.
   * @param {string} html
   */
  #splitDescriptionSections(html) {
    const source = this.#str(html);
    if (!source) {
      return { introHtml: "", sections: [] };
    }

    /** @type {{ title: string; headingKey: string; bodyHtml: string }[]} */
    const sections = [];
    const regex = /<h([1-3])[^>]*>([\s\S]*?)<\/h[1-3]>/gi;
    const matches = [...source.matchAll(regex)];

    if (matches.length === 0) {
      return { introHtml: source, sections: [] };
    }

    const firstIdx = matches[0].index ?? 0;
    const introHtml = source.slice(0, firstIdx).trim();

    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const titleRaw = m[2] ?? "";
      const titleEl = document.createElement("div");
      titleEl.innerHTML = titleRaw;
      const title = this.#str(titleEl.textContent);
      const headingKey = this.#normalizeHeading(title);
      const start = (m.index ?? 0) + m[0].length;
      const end =
        i + 1 < matches.length
          ? (matches[i + 1].index ?? source.length)
          : source.length;
      const bodyHtml = source.slice(start, end).trim();
      sections.push({ title, headingKey, bodyHtml });
    }

    return { introHtml, sections };
  }

  /**
   * @param {{ title: string; headingKey: string; bodyHtml: string }[]} sections
   * @param {string[]} includesTerms
   */
  #pluckSectionByHeading(sections, includesTerms) {
    const idx = sections.findIndex((section) =>
      includesTerms.some((term) => section.headingKey.includes(term)),
    );
    if (idx < 0) return null;
    const [picked] = sections.splice(idx, 1);
    return picked;
  }

  /**
   * Route API rich-content rows into the matching tab bucket.
   * @param {unknown} raw
   * @returns {'description' | 'features' | 'kit' | 'instructions'}
   */
  #richContentBucket(raw) {
    if (!raw || typeof raw !== "object") return "features";

    const item = /** @type {Record<string, unknown>} */ (raw);
    const candidates = [
      item.Tab,
      item.TabName,
      item.Section,
      item.SectionName,
      item.Category,
      item.Type,
      item.Title,
      item.Name,
      item.ContentDescription,
      item.contentDescription,
      item.FileName,
      item.fileName,
    ];
    const haystack = this.#normalizeLabel(
      candidates.map((value) => this.#str(value)).join(" "),
    );

    if (
      haystack.includes("description") ||
      haystack.includes("overview") ||
      haystack.includes("product details") ||
      haystack.includes("about")
    ) {
      return "description";
    }

    if (
      haystack.includes("kit") ||
      haystack.includes("in the box") ||
      haystack.includes("what s in") ||
      haystack.includes("what is in")
    ) {
      return "kit";
    }

    if (
      haystack.includes("instruction") ||
      haystack.includes("install") ||
      haystack.includes("download") ||
      haystack.includes("video") ||
      haystack.includes("fitment guide") ||
      haystack.includes("brochure") ||
      haystack.includes("manual") ||
      haystack.includes("datasheet") ||
      haystack.includes("drawing") ||
      haystack.includes("warranty card")
    ) {
      return "instructions";
    }

    return "features";
  }

  /**
   * @param {unknown} raw
   * @returns {{description: unknown[]; features: unknown[]; kit: unknown[]; instructions: unknown[]}}
   */
  #groupRichContents(raw) {
    const grouped = {
      description: [],
      features: [],
      kit: [],
      instructions: [],
    };
    const rows = this.#normalizeArray(raw);
    for (const row of rows) {
      const bucket = this.#richContentBucket(row);
      grouped[bucket].push(row);
    }
    return grouped;
  }

  /**
   * Rows in `ProductRichContents` sometimes carry PDF/document URLs (same shape as Downloads).
   * Pull those into the instructions download list and keep gallery-style image rows for tab HTML.
   * @param {unknown} raw
   * @returns {{ richRows: unknown[]; supplementalDownloadRows: unknown[] }}
   */
  #splitProductRichContentsForTabs(raw) {
    const rows = this.#normalizeArray(raw);
    const richRows = [];
    const supplementalDownloadRows = [];

    const docHint =
      /brochure|manual|instruction|install|datasheet|spec(ific|ification)?|drawing|document|guide|catalog|worksheet|warranty|pdf/i;
    const productImageDesc = /^(product\s*)?image$/i;

    for (const row of rows) {
      if (!row || typeof row !== "object") {
        richRows.push(row);
        continue;
      }
      const item = /** @type {Record<string, unknown>} */ (row);
      const url = this.#str(item.ContentURL ?? item.contentURL);
      if (!/^https?:\/\//i.test(url)) {
        richRows.push(row);
        continue;
      }

      const desc = this.#str(
        item.ContentDescription ?? item.contentDescription,
      );
      const fname = this.#str(item.FileName ?? item.fileName);
      const title = this.#str(
        item.Title ?? item.title ?? item.Name ?? item.name,
      );
      const hasThumb = Boolean(
        this.#str(item.ContentURLThumb ?? item.contentURLThumb),
      );

      const looksLikeGalleryImage =
        hasThumb &&
        !/\.pdf$/i.test(fname) &&
        (productImageDesc.test(desc) || (!fname && !docHint.test(desc)));

      if (looksLikeGalleryImage) {
        richRows.push(row);
        continue;
      }

      const blob = `${desc} ${fname} ${title}`;
      if (docHint.test(blob) || /\.pdf$/i.test(fname)) {
        supplementalDownloadRows.push(row);
        continue;
      }

      richRows.push(row);
    }

    return { richRows, supplementalDownloadRows };
  }

  /**
   * @param {HTMLElement} el
   * @param {string} html
   * @param {boolean} asHtml
   */
  #setPanelContent(el, html, asHtml) {
    const emptyMsg = this.dataset.emptyTabMessage || "";
    el.innerHTML = "";

    const trimmed = (html || "").trim();
    if (!trimmed) {
      const p = document.createElement("p");
      p.className = "airbag-product-tabs__empty";
      p.textContent = emptyMsg;
      el.appendChild(p);
      return;
    }

    if (asHtml) {
      el.innerHTML = trimmed;
    } else {
      const wrap = document.createElement("div");
      wrap.textContent = trimmed;
      el.appendChild(wrap);
    }
  }

  /**
   * @param {HTMLElement} container
   */
  #removeEmptyPlaceholder(container) {
    if (container.childElementCount <= 1) return;
    const empty = container.querySelector(
      ":scope > .airbag-product-tabs__empty",
    );
    if (empty instanceof HTMLElement) {
      empty.remove();
    }
  }

  /**
   * @param {HTMLElement} container
   * @param {unknown} items
   * @param {boolean} asHtml
   * @param {boolean} [append]
   */
  #renderRichBlocks(container, items, asHtml, append = false) {
    if (!append) container.innerHTML = "";
    if (!Array.isArray(items) || items.length === 0) return false;

    let any = false;
    for (const raw of items) {
      if (!raw || typeof raw !== "object") continue;
      const item = /** @type {Record<string, unknown>} */ (raw);
      const title = this.#str(
        item.Title ?? item.title ?? item.Name ?? item.name,
      );
      const body =
        this.#str(
          item.Html ??
            item.HTML ??
            item.Body ??
            item.Content ??
            item.Description ??
            item.Text,
        ) || "";

      if (!title && !body) continue;
      any = true;

      const section = document.createElement("section");
      section.className = "airbag-product-tabs__rich-block";

      if (title) {
        const h = document.createElement("h4");
        h.className = "airbag-product-tabs__rich-heading";
        h.textContent = title;
        section.appendChild(h);
      }

      if (body) {
        const div = document.createElement("div");
        div.className = "airbag-product-tabs__rich-body rte";
        if (asHtml) {
          div.innerHTML = body;
        } else {
          div.textContent = body;
        }
        section.appendChild(div);
      }

      container.appendChild(section);
    }

    return any;
  }

  /**
   * @param {HTMLElement} container
   * @param {Record<string, unknown>} product
   */
  #renderInstructions(container, product) {
    container.innerHTML = "";
    const fragment = document.createDocumentFragment();

    this.#appendLegacyManualAndInstructionRows(fragment);
    this.#appendGeneratedBrochureRow(fragment, product);

    container.appendChild(fragment);

    if (!container.textContent?.trim()) {
      const p = document.createElement("p");
      p.className = "airbag-product-tabs__empty";
      p.textContent = this.dataset.emptyTabMessage || "";
      container.appendChild(p);
    }
  }

  /**
   * @param {Record<string, unknown>} row
   * @returns {string}
   */
  #applicationModelLabel(row) {
    const model = this.#pick(row, "Model", "model");
    const chassis = this.#pick(
      row,
      "Chassis",
      "chassis",
      "ModelChassis",
      "ModelAndChassis",
      "modelChassis",
    );
    return [model, chassis].filter(Boolean).join(" ");
  }

  /**
   * @param {Record<string, unknown>} row
   * @returns {string}
   */
  #applicationOutcomeLabel(row) {
    return this.#pick(row, "Height", "height", "Outcome", "outcome");
  }

  /**
   * Direct product URLs (no collection): list every vehicle application from the API.
   * @param {HTMLElement} container
   * @param {Record<string, unknown>} product
   */
  #renderApplication(container, product) {
    container.innerHTML = "";

    if (this.#isCollectionContext()) return;

    const items = this.#normalizeArray(product.Application);
    if (items.length === 0) {
      const p = document.createElement("p");
      p.className = "airbag-product-tabs__empty";
      p.textContent = this.dataset.emptyTabMessage || "";
      container.appendChild(p);
      return;
    }

    const makeLabel = this.dataset.labelAppMake || "Make";
    const modelLabel = this.dataset.labelAppModel || "Model";
    const outcomeLabel = this.dataset.labelAppOutcome || "Height";
    const caption =
      this.dataset.applicationTableCaption ||
      this.dataset.labelApplication ||
      "";

    const wrapper = document.createElement("div");
    wrapper.className = "rte-table-wrapper";

    const table = document.createElement("table");
    table.className = "airbag-product-tabs__application-table";

    const captionEl = document.createElement("caption");
    captionEl.textContent = decodeHtmlEntities(caption);
    table.appendChild(captionEl);

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    for (const label of [makeLabel, modelLabel, outcomeLabel]) {
      const th = document.createElement("th");
      th.scope = "col";
      th.textContent = decodeHtmlEntities(label);
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    let rowCount = 0;

    for (const rawRow of items) {
      if (!rawRow || typeof rawRow !== "object") continue;
      const row = /** @type {Record<string, unknown>} */ (rawRow);
      const make = decodeHtmlEntities(this.#pick(row, "Make", "make"));
      const model = decodeHtmlEntities(this.#applicationModelLabel(row));
      const outcome = decodeHtmlEntities(this.#applicationOutcomeLabel(row));
      if (!make && !model && !outcome) continue;

      const tr = document.createElement("tr");
      for (const value of [make, model, outcome]) {
        const td = document.createElement("td");
        td.textContent = value;
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
      rowCount += 1;
    }

    if (rowCount === 0) {
      const p = document.createElement("p");
      p.className = "airbag-product-tabs__empty";
      p.textContent = this.dataset.emptyTabMessage || "";
      container.appendChild(p);
      return;
    }

    table.appendChild(tbody);
    wrapper.appendChild(table);
    container.appendChild(wrapper);
  }

  /**
   * @param {HTMLElement} container
   * @param {unknown} kitsRaw
   */
  #renderKit(container, kitsRaw, kitStrings, asHtml) {
    container.innerHTML = "";

    for (const s of kitStrings) {
      if (!this.#str(s)) continue;
      const div = document.createElement("div");
      div.className = "airbag-product-tabs__kit-html rte";
      if (asHtml) {
        div.innerHTML = /** @type {string} */ (s);
      } else {
        div.textContent = /** @type {string} */ (s);
      }
      container.appendChild(div);
    }

    const kits = this.#normalizeArray(kitsRaw);
    if (kits.length > 0) {
      const ul = document.createElement("ul");
      ul.className = "airbag-product-tabs__kit-list";

      for (const raw of kits) {
        if (raw == null) continue;
        if (typeof raw === "string" || typeof raw === "number") {
          const li = document.createElement("li");
          li.textContent = String(raw);
          ul.appendChild(li);
          continue;
        }

        if (typeof raw !== "object") continue;
        const item = /** @type {Record<string, unknown>} */ (raw);
        const line =
          this.#pick(
            item,
            "Title",
            "Name",
            "Description",
            "PartNumber",
            "PartNr",
            "Label",
          ) || JSON.stringify(item);
        const li = document.createElement("li");
        li.textContent = line;
        ul.appendChild(li);
      }

      if (ul.childElementCount > 0) {
        container.appendChild(ul);
      }
    }

    if (!container.innerHTML.trim()) {
      const p = document.createElement("p");
      p.className = "airbag-product-tabs__empty";
      p.textContent = this.dataset.emptyTabMessage || "";
      container.appendChild(p);
    }
  }

  /**
   * @param {Record<string, unknown>} product
   */
  #fillPanels(product) {
    const allowHtml = this.dataset.allowHtml === "true";
    const root = this.refs.contentWrap;
    if (!(root instanceof HTMLElement)) return;

    const descEl = root.querySelector('[data-airbag-tab-panel="description"]');
    const appEl = root.querySelector('[data-airbag-tab-panel="application"]');
    const featEl = root.querySelector('[data-airbag-tab-panel="features"]');
    const kitEl = root.querySelector('[data-airbag-tab-panel="kit"]');
    const insEl = root.querySelector('[data-airbag-tab-panel="instructions"]');

    if (
      !(
        descEl instanceof HTMLElement &&
        featEl instanceof HTMLElement &&
        kitEl instanceof HTMLElement &&
        insEl instanceof HTMLElement
      )
    ) {
      return;
    }

    const { richRows } = this.#splitProductRichContentsForTabs(
      product.ProductRichContents,
    );
    const richByTab = this.#groupRichContents(richRows);

    let descHtml = this.#pick(
      product,
      "PRetailsDescription",
      "Description",
      "LongDescription",
      "ProductDescription",
    );
    if (!descHtml && this.refs.shopifyDescriptionFallback) {
      const fb = this.refs.shopifyDescriptionFallback.innerHTML.trim();
      if (fb && this.dataset.shopifyDescFallback === "true") {
        descHtml = fb;
      }
    }

    const parsedDescription = this.#splitDescriptionSections(descHtml);
    const remainingSections = [...parsedDescription.sections];
    const featuresSection = this.#pluckSectionByHeading(remainingSections, [
      "features benefits",
      "features and benefits",
    ]);
    const kitSection = this.#pluckSectionByHeading(remainingSections, [
      "what s in the kit",
      "whats in the kit",
      "kit",
    ]);

    let composedDescriptionHtml = "";
    if (parsedDescription.introHtml) {
      composedDescriptionHtml = parsedDescription.introHtml;
    } else if (remainingSections.length > 0) {
      composedDescriptionHtml = remainingSections
        .map(
          (section) =>
            `<h4 class="airbag-product-tabs__rich-heading">${section.title}</h4><div class="rte">${section.bodyHtml}</div>`,
        )
        .join("");
    } else {
      composedDescriptionHtml = descHtml;
    }

    if (appEl instanceof HTMLElement) {
      this.#renderApplication(appEl, product);
    }

    if (composedDescriptionHtml) {
      this.#setPanelContent(descEl, composedDescriptionHtml, true);
      this.#renderRichBlocks(descEl, richByTab.description, allowHtml, true);
    } else {
      descEl.innerHTML = "";
      const descRich = this.#renderRichBlocks(
        descEl,
        richByTab.description,
        allowHtml,
        true,
      );
      if (!descRich) {
        this.#setPanelContent(descEl, "", false);
      }
    }

    featEl.innerHTML = "";
    let featFilled = false;
    const featStr = this.#pick(
      product,
      "FeaturesBenefits",
      "FeaturesAndBenefits",
      "Features_HTML",
      "BenefitsHtml",
      "Features",
    );
    const resolvedFeatureHtml = featStr || featuresSection?.bodyHtml || "";
    if (resolvedFeatureHtml) {
      const intro = document.createElement("div");
      intro.className = "rte";
      if (allowHtml) {
        intro.innerHTML = resolvedFeatureHtml;
      } else {
        intro.textContent = resolvedFeatureHtml;
      }
      featEl.appendChild(intro);
      featFilled = true;
    }

    const richOk = this.#renderRichBlocks(
      featEl,
      richByTab.features,
      allowHtml,
      true,
    );
    featFilled = featFilled || richOk;

    if (!featFilled) {
      this.#setPanelContent(featEl, "", false);
    }

    const kitStrings = [
      this.#pick(
        product,
        "WhatsInTheKit",
        "Whats_In_The_Kit",
        "KitContents",
        "Kit_Contents",
        "InTheBox",
      ),
      kitSection?.bodyHtml || "",
    ].filter(Boolean);

    this.#renderKit(
      kitEl,
      product.AirControlKits ?? product.KitItems ?? product.KitLines,
      kitStrings,
      allowHtml,
    );
    this.#renderRichBlocks(kitEl, richByTab.kit, allowHtml, true);
    this.#removeEmptyPlaceholder(kitEl);

    this.#renderInstructions(insEl, product);
    this.#renderRichBlocks(insEl, richByTab.instructions, allowHtml, true);
    this.#removeEmptyPlaceholder(insEl);
  }

  async #load() {
    const nr = this.#currentNr();
    const base = (this.dataset.apiBase || "").trim();
    const requestId = ++this.#requestId;
    this.#showSection();

    if (!nr) {
      this.#presentFallbackOnly();
      return;
    }

    if (!base) {
      this.#presentFallbackOnly();
      return;
    }

    this.#enterLoadingUi();

    try {
      const mmyContext = this.#effectiveMmyContext();
      const vehicleParams = this.#vehicleParamsFromContext(mmyContext);

      const data = await fetchAirbagProductDetails({
        apiBase: base,
        productNr: nr,
        vehicle: vehicleParams,
      });
      if (requestId !== this.#requestId) return;

      const product = /** @type {Record<string, unknown>} */ (
        data?.product ?? data ?? {}
      );
      this.#fillPanels(product);
      const visibleTabs = this.#applyTabVisibility();
      this.#exitLoadingUi();
      if (visibleTabs === 0) {
        this.#hideSection();
        return;
      }
    } catch (e) {
      if (requestId !== this.#requestId) return;
      this.#presentFallbackOnly();
    }
  }

  /**
   * Show placeholder copy in panels when API cannot run or fails.
   * Reuses #fillPanels({}) so Shopify description fallback is split into tabs (h1–h3) like API HTML.
   */
  #presentFallbackOnly() {
    this.#fillPanels({});
    const visibleTabs = this.#applyTabVisibility();
    this.#exitLoadingUi();
    if (visibleTabs === 0) {
      this.#hideSection();
      return;
    }
  }
}

if (!customElements.get("airbag-product-tabs")) {
  customElements.define("airbag-product-tabs", AirbagProductTabs);
}
